import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import { lockShadowCasting } from "./shadowProxy";

/**
 * The props toolkit: caches, vertex-coloured finishes, a part list that merges per material, and the shape helpers
 * every prop is built from. Units are metres; +z is the road ahead, y is up. A prop is built once per key and then
 * cloned, so every instance shares its geometry and materials.
 */

export type V2 = [number, number];
export type V3 = [number, number, number];

const store = new Map<string, unknown>();

/** Memoise by key: geometries, materials, textures and prototypes are made once and shared. */
export function once<T>(key: string, make: () => T): T {
  if (store.has(key)) return store.get(key) as T;
  const value = make();
  store.set(key, value);
  return value;
}

/** A clone of a cached prototype: shares geometry and materials (userData is copied as JSON). */
export function instance(key: string, make: () => THREE.Group): THREE.Group {
  const copy = once(`proto:${key}`, make).clone(true);
  lockShadowCasting(copy);                       // a prototype with a shadow proxy: only the proxy casts
  return copy;
}

// ------------------------------------------------------------------------------------------------ colour

/** sRGB hex → working-space colour (what vertex colours and material colours expect). */
export const col = (c: number | string | THREE.Color): THREE.Color => (c instanceof THREE.Color ? c.clone() : new THREE.Color(c));

/** A colour pushed above 1 (for lamps and glows: bright, and bloom-ready when the scene has bloom). */
export const hdr = (c: number | string, k: number): THREE.Color => new THREE.Color(c).multiplyScalar(k);

const srgb = (hex: string): V3 => [parseInt(hex.slice(1, 3), 16) / 255, parseInt(hex.slice(3, 5), 16) / 255, parseInt(hex.slice(5, 7), 16) / 255];

/** A gradient through sRGB stops (blended in sRGB, like the logo artwork), returned in working space. */
export function ramp(stops: [number, string][], t: number): THREE.Color {
  const x = Math.min(1, Math.max(0, t));
  let i = 0;
  while (i < stops.length - 2 && x > stops[i + 1][0]) i++;
  const [t0, c0] = stops[i];
  const [t1, c1] = stops[i + 1];
  const k = t1 > t0 ? Math.min(1, Math.max(0, (x - t0) / (t1 - t0))) : 0;
  const a = srgb(c0), b = srgb(c1);
  return new THREE.Color().setRGB(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k, THREE.SRGBColorSpace);
}

/** The Solana gradient (the mark's own stops, purple → green). */
export const SOL_STOPS: [number, string][] = [[0, "#9945ff"], [0.3, "#8752f3"], [0.5, "#5497d5"], [0.6, "#43b4ca"], [0.72, "#28e0b9"], [1, "#19fb9b"]];
export const solGradient = (t: number): THREE.Color => ramp(SOL_STOPS, t);

export const PALETTE = {
  solPurple: 0x9945ff,
  solGreen: 0x14f195,
  ethRed: 0xff1f3d,
  hazard: 0xffc21a,
  ink: 0x17181f,
  white: 0xf4f5f9,
  steel: 0x9aa1b1,
  darkSteel: 0x3b4152,
  rubber: 0x1c1d22,
  chrome: 0xeef1f6,
  gold: 0xffc53d
} as const;

// ------------------------------------------------------------------------------------------------ finishes

/**
 * Finishes are not separate materials: every lit, untextured part goes into ONE shared MeshStandardMaterial that
 * reads its roughness, metalness and a self-glow factor from a per-vertex `pbr` attribute. So a prop is one opaque
 * mesh + one unlit `glow` mesh + its decals, and the whole set compiles a single shader program.
 */
export type Finish = "paint" | "gloss" | "plastic" | "satin" | "metal" | "chrome" | "rubber" | "glass" | "glow" | "matte";

/** A custom finish: roughness, metalness, self-glow (0 = none; 0.2 = collectible-bright). */
export interface Surface { r: number; m: number; e?: number; }

const FINISHES: Record<Exclude<Finish, "glow">, Surface> = {
  paint: { r: 0.42, m: 0.06 },
  gloss: { r: 0.26, m: 0.08 },
  plastic: { r: 0.62, m: 0 },
  matte: { r: 0.86, m: 0 },
  satin: { r: 0.45, m: 0.55 },
  metal: { r: 0.26, m: 0.92 },
  chrome: { r: 0.1, m: 1 },
  rubber: { r: 0.9, m: 0 },
  glass: { r: 0.12, m: 0.42 }
};

/** The one lit material (vertex colour × per-vertex roughness / metalness / self-glow). */
export const pbrMaterial = (): THREE.MeshStandardMaterial => once("mat:pbr", () => {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0 });
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nattribute vec3 pbr;\nvarying vec3 vPbr;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\n\tvPbr = pbr;");
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vPbr;")
      .replace("#include <roughnessmap_fragment>", "float roughnessFactor = vPbr.x;")
      .replace("#include <metalnessmap_fragment>", "float metalnessFactor = vPbr.y;")
      .replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += diffuseColor.rgb * vPbr.z;");
  };
  m.customProgramCacheKey = () => "solrun-props-pbr";
  return m;
});

/** Unlit, not tone-mapped vertex colour: lamps, LEDs, neon (colours above 1 bloom when the scene has bloom). */
export const glowMaterial = (): THREE.MeshBasicMaterial => once("mat:glowv", () => new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }));

/** The material a finish resolves to (kept for code that wants a standalone material). */
export const finish = (f: Finish): THREE.Material => (f === "glow" ? glowMaterial() : pbrMaterial());

// ------------------------------------------------------------------------------------------------ texture atlas

/** A source texture's place in an atlas, in the atlas's uv: [u0, v0, du, dv]; du = dv = 0 is the white texel. */
export type Region = [number, number, number, number];

export interface Atlas {
  texture: THREE.CanvasTexture;
  regions: Map<THREE.Texture, Region>;
  white: Region;
  /** the tiling normal map (own texture, repeat-wrapped) the atlas material applies where a part's `corr` is 1 */
  normalMap?: THREE.Texture;
  material: THREE.MeshStandardMaterial;
}

/**
 * One canvas holding several clamp-wrapped canvas textures (8-px edge gutters, a white block for untextured parts)
 * and the material that draws a whole prop with it: vertex colour × the part's region of the atlas (its own uv 0–1,
 * clamped like ClampToEdge, sampled with its own derivatives so the mip level matches the source texture's) ×
 * per-vertex roughness / metalness / self-glow (attribute pbr), the normal map scaled per vertex (attribute corr).
 */
export function atlasOf(key: string, sources: THREE.Texture[], normal?: { map: THREE.Texture; scale: number }): Atlas {
  return once(`atlas:${key}`, () => {
    const GUT = 8;
    const items = sources.map((t) => { const img = t.image as HTMLCanvasElement; return { t, img, w: img.width, h: img.height }; });
    const order = [...items].sort((a, b) => b.h - a.h);
    let W = 256;
    while (order.some((i) => i.w + 2 * GUT > W)) W *= 2;
    // shelf packing; the white block last
    const spots = new Map<THREE.Texture | null, { x: number; y: number }>();
    let x = 0, y = 0, rowH = 0;
    for (const it of [...order, { t: null, img: null, w: 16, h: 16 }]) {
      if (x + it.w + 2 * GUT > W) { x = 0; y += rowH; rowH = 0; }
      spots.set(it.t, { x: x + GUT, y: y + GUT });
      x += it.w + 2 * GUT;
      rowH = Math.max(rowH, it.h + 2 * GUT);
    }
    let H = 64;
    while (H < y + rowH) H *= 2;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    const regions = new Map<THREE.Texture, Region>();
    if (ctx) {
      ctx.imageSmoothingEnabled = false;
      for (const { t, img, w, h } of items) {
        const at = spots.get(t);
        if (!at) continue;
        const { x: dx, y: dy } = at;
        ctx.drawImage(img, dx, dy);
        // gutters: the edge pixels stretched outwards (what ClampToEdge would sample there)
        ctx.drawImage(img, 0, 0, 1, h, dx - GUT, dy, GUT, h);
        ctx.drawImage(img, w - 1, 0, 1, h, dx + w, dy, GUT, h);
        ctx.drawImage(img, 0, 0, w, 1, dx, dy - GUT, w, GUT);
        ctx.drawImage(img, 0, h - 1, w, 1, dx, dy + h, w, GUT);
        ctx.drawImage(img, 0, 0, 1, 1, dx - GUT, dy - GUT, GUT, GUT);
        ctx.drawImage(img, w - 1, 0, 1, 1, dx + w, dy - GUT, GUT, GUT);
        ctx.drawImage(img, 0, h - 1, 1, 1, dx - GUT, dy + h, GUT, GUT);
        ctx.drawImage(img, w - 1, h - 1, 1, 1, dx + w, dy + h, GUT, GUT);
        regions.set(t, [dx / W, 1 - (dy + h) / H, w / W, h / H]);
      }
      const wb = spots.get(null);
      if (wb) { ctx.fillStyle = "#ffffff"; ctx.fillRect(wb.x - GUT, wb.y - GUT, 16 + 2 * GUT, 16 + 2 * GUT); }
    }
    const wb = spots.get(null) ?? { x: 0, y: 0 };
    const white: Region = [(wb.x + 8) / W, 1 - (wb.y + 8) / H, 0, 0];
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = Math.max(1, ...sources.map((t) => t.anisotropy));
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true, map: texture, roughness: 0.5, metalness: 0,
      normalMap: normal?.map ?? null, normalScale: new THREE.Vector2(normal?.scale ?? 1, normal?.scale ?? 1)
    });
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nattribute vec3 pbr;\nattribute vec4 atlas;\nattribute float corr;\nvarying vec3 vPbr;\nvarying vec4 vAtlas;\nvarying float vCorr;\nvarying vec2 vAtlasUv;")
        .replace("#include <begin_vertex>", "#include <begin_vertex>\n\tvPbr = pbr; vAtlas = atlas; vCorr = corr; vAtlasUv = uv;");
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying vec3 vPbr;\nvarying vec4 vAtlas;\nvarying float vCorr;\nvarying vec2 vAtlasUv;")
        .replace("#include <map_fragment>", "diffuseColor *= textureGrad(map, vAtlas.xy + clamp(vAtlasUv, 0.0, 1.0) * vAtlas.zw, dFdx(vAtlasUv) * vAtlas.zw, dFdy(vAtlasUv) * vAtlas.zw);")
        .replace("#include <roughnessmap_fragment>", "float roughnessFactor = vPbr.x;")
        .replace("#include <metalnessmap_fragment>", "float metalnessFactor = vPbr.y;")
        .replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += diffuseColor.rgb * vPbr.z;")
        .replace("#include <normal_fragment_maps>", THREE.ShaderChunk.normal_fragment_maps.replace("mapN.xy *= normalScale;", "mapN.xy *= normalScale * vCorr;"));
    };
    material.customProgramCacheKey = () => `solrun-props-atlas:${key}`;
    return { texture, regions, white, normalMap: normal?.map, material };
  });
}

// ------------------------------------------------------------------------------------------------ the part list

export interface Place {
  p?: V3;
  r?: V3;
  s?: number | V3;
}

export type Paint = number | string | THREE.Color | ((position: THREE.Vector3, normal: THREE.Vector3) => THREE.Color);

const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const _s = new THREE.Vector3();

export function matrixOf(at: Place): THREE.Matrix4 {
  const s = at.s ?? 1;
  _s.set(...(typeof s === "number" ? [s, s, s] as V3 : s));
  _q.setFromEuler(_e.set(...(at.r ?? [0, 0, 0])));
  return new THREE.Matrix4().compose(_v.set(...(at.p ?? [0, 0, 0])), _q, _s);
}

function flipWinding(g: THREE.BufferGeometry): void {
  for (const name of Object.keys(g.attributes)) {
    const attr = g.getAttribute(name) as THREE.BufferAttribute;
    const a = attr.array as Float32Array;
    const size = attr.itemSize;
    for (let t = 0; t < attr.count; t += 3) {
      for (let k = 0; k < size; k++) {
        const i1 = (t + 1) * size + k, i2 = (t + 2) * size + k;
        const tmp = a[i1]; a[i1] = a[i2]; a[i2] = tmp;
      }
    }
    attr.needsUpdate = true;
  }
}

const _pos = new THREE.Vector3();
const _nrm = new THREE.Vector3();

/** A copy ready to merge: non-indexed, position/normal/uv/color (+ pbr) only, transformed and painted. */
function prep(source: THREE.BufferGeometry, paint: Paint, at?: Place | THREE.Matrix4, surface?: Surface, extra?: { atlas: Region; corr: number }): THREE.BufferGeometry {
  const g = source.index ? source.toNonIndexed() : source.clone();
  for (const name of Object.keys(g.attributes)) if (name !== "position" && name !== "normal" && name !== "uv") g.deleteAttribute(name);
  g.morphAttributes = {};
  g.clearGroups();
  if (!g.getAttribute("normal")) g.computeVertexNormals();
  const count = g.getAttribute("position").count;
  if (!g.getAttribute("uv")) g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(count * 2), 2));
  if (at) {
    const m = at instanceof THREE.Matrix4 ? at : matrixOf(at);
    g.applyMatrix4(m);
    if (m.determinant() < 0) flipWinding(g);
  }
  const colors = new Float32Array(count * 3);
  if (typeof paint === "function") {
    const p = g.getAttribute("position"), n = g.getAttribute("normal");
    for (let i = 0; i < count; i++) {
      const c = paint(_pos.fromBufferAttribute(p, i), _nrm.fromBufferAttribute(n, i));
      colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
    }
  } else {
    const c = col(paint);
    for (let i = 0; i < count; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  }
  g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  if (surface) {
    const pbr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) { pbr[i * 3] = surface.r; pbr[i * 3 + 1] = surface.m; pbr[i * 3 + 2] = surface.e ?? 0; }
    g.setAttribute("pbr", new THREE.BufferAttribute(pbr, 3));
  }
  if (extra) {
    const region = new Float32Array(count * 4), corr = new Float32Array(count).fill(extra.corr);
    for (let i = 0; i < count; i++) region.set(extra.atlas, i * 4);
    g.setAttribute("atlas", new THREE.BufferAttribute(region, 4));
    g.setAttribute("corr", new THREE.BufferAttribute(corr, 1));
  }
  return g;
}

/**
 * Parts are added with a finish (or any material) and a colour, placed by position/rotation/scale; `build()` merges
 * everything that shares a material into one geometry. So a truck is ~8 draw calls however many bolts it has.
 */
export class Kit {
  private buckets = new Map<THREE.Material, THREE.BufferGeometry[]>();

  /** `atlas`: every lit part — finishes and the textured materials the atlas holds — goes into the atlas's one
   *  material (see atlasOf), so the prop is one lit mesh + one glow mesh. */
  constructor(private readonly atlas?: Atlas) {}

  /** `material`: a finish name, a custom Surface (both → the shared PBR material), "glow", or any material (decals). */
  add(geometry: THREE.BufferGeometry, material: Finish | Surface | THREE.Material, paint: Paint = 0xffffff, at?: Place | THREE.Matrix4): this {
    let m: THREE.Material;
    let surface: Surface | undefined;
    let extra: { atlas: Region; corr: number } | undefined;
    const atlas = this.atlas;
    if (material === "glow") m = glowMaterial();
    else if (typeof material === "string" || !(material as THREE.Material).isMaterial) {
      surface = typeof material === "string" ? FINISHES[material] : material as Surface;
      if (atlas) { m = atlas.material; extra = { atlas: atlas.white, corr: 0 }; } else m = pbrMaterial();
    } else {
      // a textured standard material the atlas holds (its map in a region, or the atlas's tiling normal map): merged
      const std = material as THREE.MeshStandardMaterial;
      const plain = std.isMeshStandardMaterial === true && !std.emissiveMap && std.color.getHex() === 0xffffff && !std.alphaMap;
      const region = atlas && plain && std.map ? atlas.regions.get(std.map) : undefined;
      const corr = atlas && plain && !!std.normalMap && std.normalMap === atlas.normalMap;
      if (atlas && plain && (region || (corr && !std.map)) && (!std.normalMap || corr)) {
        m = atlas.material;
        surface = { r: std.roughness, m: std.metalness };
        extra = { atlas: region ?? atlas.white, corr: corr ? 1 : 0 };
        if (!std.vertexColors) paint = 0xffffff;
      } else m = material as THREE.Material;
    }
    const list = this.buckets.get(m) ?? [];
    list.push(prep(geometry, paint, at, surface, extra));
    this.buckets.set(m, list);
    return this;
  }

  /** One mesh per material. `receive` marks surfaces the car's shadow should fall on (roofs, decks). */
  build(opts: { receive?: boolean; name?: string } = {}): THREE.Group {
    const group = new THREE.Group();
    if (opts.name) group.name = opts.name;
    for (const [material, list] of this.buckets) {
      const merged = list.length === 1 ? list[0] : mergeGeometries(list, false);
      if (!merged) continue;
      merged.computeBoundingSphere();
      merged.computeBoundingBox();
      const mesh = new THREE.Mesh(merged, material);
      const unlit = (material as THREE.MeshBasicMaterial).isMeshBasicMaterial === true;
      mesh.castShadow = !unlit && !material.transparent;
      mesh.receiveShadow = opts.receive === true && !unlit;
      group.add(mesh);
    }
    return group;
  }
}

// ------------------------------------------------------------------------------------------------ primitives

/** A box with flat 45° chamfers on every edge and corner (44 triangles): crisp highlights on hard surfaces. */
export function chamferBox(w: number, h: number, d: number, b: number): THREE.BufferGeometry {
  return once(`g:chamfer:${w}:${h}:${d}:${b}`, () => {
    const x = w / 2, y = h / 2, z = d / 2;
    const c = Math.min(b, x * 0.49, y * 0.49, z * 0.49);
    const pts: THREE.Vector3[] = [];
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
      pts.push(new THREE.Vector3(sx * x, sy * (y - c), sz * (z - c)));
      pts.push(new THREE.Vector3(sx * (x - c), sy * y, sz * (z - c)));
      pts.push(new THREE.Vector3(sx * (x - c), sy * (y - c), sz * z));
    }
    const g = new ConvexGeometry(pts);
    boxUV(g, 1);
    return g;
  });
}

/** Box-projected UVs in metres × `scale` (u/v from the two axes across each face's dominant normal). */
export function boxUV(g: THREE.BufferGeometry, scale: number): THREE.BufferGeometry {
  const p = g.getAttribute("position"), n = g.getAttribute("normal");
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u: number, v: number;
    if (ax >= ay && ax >= az) { u = p.getZ(i) * Math.sign(n.getX(i) || 1); v = p.getY(i); }
    else if (ay >= az) { u = p.getX(i); v = p.getZ(i) * Math.sign(n.getY(i) || 1); }
    else { u = -p.getX(i) * Math.sign(n.getZ(i) || 1); v = p.getY(i); }
    uv[i * 2] = u * scale; uv[i * 2 + 1] = v * scale;
  }
  g.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  return g;
}

export const G = {
  box: (w: number, h: number, d: number) => once(`g:box:${w}:${h}:${d}`, () => new THREE.BoxGeometry(w, h, d)),
  chamfer: chamferBox,
  round: (w: number, h: number, d: number, r: number, seg = 2) => once(`g:round:${w}:${h}:${d}:${r}:${seg}`, () => new RoundedBoxGeometry(w, h, d, seg, r)),
  cyl: (rt: number, rb: number, h: number, seg = 16, open = false) => once(`g:cyl:${rt}:${rb}:${h}:${seg}:${open}`, () => new THREE.CylinderGeometry(rt, rb, h, seg, 1, open)),
  sphere: (r: number, ws = 16, hs = 12) => once(`g:sphere:${r}:${ws}:${hs}`, () => new THREE.SphereGeometry(r, ws, hs)),
  hemi: (r: number, ws = 16, hs = 6) => once(`g:hemi:${r}:${ws}:${hs}`, () => new THREE.SphereGeometry(r, ws, hs, 0, Math.PI * 2, 0, Math.PI / 2)),
  torus: (R: number, r: number, rs = 8, ts = 24, arc = Math.PI * 2) => once(`g:torus:${R}:${r}:${rs}:${ts}:${arc}`, () => new THREE.TorusGeometry(R, r, rs, ts, arc)),
  plane: (w: number, h: number) => once(`g:plane:${w}:${h}`, () => new THREE.PlaneGeometry(w, h)),
  circle: (r: number, seg = 24) => once(`g:circle:${r}:${seg}`, () => new THREE.CircleGeometry(r, seg)),
  capsule: (r: number, len: number, cs = 4, rs = 12) => once(`g:capsule:${r}:${len}:${cs}:${rs}`, () => new THREE.CapsuleGeometry(r, len, cs, rs))
};

/** Rotations that turn a plane (front = +z) to face a direction with its text reading correctly from that side. */
export const FACE: Record<"front" | "back" | "left" | "right" | "up" | "down", V3> = {
  /** faces −z: towards the oncoming camera (a prop's front) */
  front: [0, Math.PI, 0],
  /** faces +z */
  back: [0, 0, 0],
  /** faces +x (screen left in game, the VX mirror) */
  left: [0, Math.PI / 2, 0],
  /** faces −x */
  right: [0, -Math.PI / 2, 0],
  /** faces +y, reads from behind (from −z, like road markings) */
  up: [-Math.PI / 2, 0, Math.PI],
  down: [Math.PI / 2, 0, 0]
};

// ------------------------------------------------------------------------------------------------ custom shapes

/**
 * A surface of revolution about +z (or `axis`) through profile points [r, z]. Walk the profile with the solid on your
 * right (e.g. front centre → outward → down the edge → back centre). Flat across each profile segment (crisp
 * bevels), smooth around the axis; `smooth` also blends normals between segments (tyres, domes).
 */
export function revolve(profile: V2[], segments: number, opts: { smooth?: boolean; axis?: "x" | "y" | "z"; arc?: number; phase?: number } = {}): THREE.BufferGeometry {
  const arc = opts.arc ?? Math.PI * 2;
  const phase = opts.phase ?? 0;
  const segN: V2[] = [];
  for (let k = 0; k < profile.length - 1; k++) {
    const dr = profile[k + 1][0] - profile[k][0], dz = profile[k + 1][1] - profile[k][1];
    const len = Math.hypot(dr, dz) || 1;
    segN.push([-dz / len, dr / len]);
  }
  const pointN = (k: number, end: 0 | 1): V2 => {
    const own = segN[k];
    if (!opts.smooth) return own;
    const other = end === 0 ? segN[k - 1] : segN[k + 1];
    if (!other) return own;
    const x = own[0] + other[0], y = own[1] + other[1];
    const l = Math.hypot(x, y) || 1;
    return [x / l, y / l];
  };
  const pos: number[] = [], nor: number[] = [], uvs: number[] = [];
  const lengths = [0];
  for (let k = 0; k < profile.length - 1; k++) lengths.push(lengths[k] + Math.hypot(profile[k + 1][0] - profile[k][0], profile[k + 1][1] - profile[k][1]));
  const total = lengths[lengths.length - 1] || 1;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), cross = new THREE.Vector3();
  for (let k = 0; k < profile.length - 1; k++) {
    const [r0, z0] = profile[k], [r1, z1] = profile[k + 1];
    const n0 = pointN(k, 0), n1 = pointN(k, 1);
    for (let i = 0; i < segments; i++) {
      const t0 = phase + (arc * i) / segments, t1 = phase + (arc * (i + 1)) / segments;
      const V = [
        { p: [r0 * Math.cos(t0), r0 * Math.sin(t0), z0], n: [n0[0] * Math.cos(t0), n0[0] * Math.sin(t0), n0[1]], uv: [i / segments, lengths[k] / total] },
        { p: [r1 * Math.cos(t0), r1 * Math.sin(t0), z1], n: [n1[0] * Math.cos(t0), n1[0] * Math.sin(t0), n1[1]], uv: [i / segments, lengths[k + 1] / total] },
        { p: [r1 * Math.cos(t1), r1 * Math.sin(t1), z1], n: [n1[0] * Math.cos(t1), n1[0] * Math.sin(t1), n1[1]], uv: [(i + 1) / segments, lengths[k + 1] / total] },
        { p: [r0 * Math.cos(t1), r0 * Math.sin(t1), z0], n: [n0[0] * Math.cos(t1), n0[0] * Math.sin(t1), n0[1]], uv: [(i + 1) / segments, lengths[k] / total] }
      ];
      for (const [i0, i1, i2] of [[0, 1, 2], [0, 2, 3]]) {
        a.fromArray(V[i0].p); b.fromArray(V[i1].p); c.fromArray(V[i2].p);
        cross.subVectors(b, a).cross(c.clone().sub(a));
        if (cross.lengthSq() < 1e-12) continue;                 // degenerate at the axis
        const mean = new THREE.Vector3().fromArray(V[i0].n).add(new THREE.Vector3().fromArray(V[i1].n)).add(new THREE.Vector3().fromArray(V[i2].n));
        const order = cross.dot(mean) >= 0 ? [i0, i1, i2] : [i0, i2, i1];
        for (const j of order) { pos.push(...V[j].p); nor.push(...V[j].n); uvs.push(...V[j].uv); }
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  if (opts.axis === "y") g.rotateX(-Math.PI / 2);
  else if (opts.axis === "x") g.rotateY(Math.PI / 2);
  return g;
}

/** Inset a convex CCW polygon by `d`. */
function insetConvex(points: V2[], d: number): V2[] {
  const n = points.length;
  const lines = points.map((p, i) => {
    const q = points[(i + 1) % n];
    const dx = q[0] - p[0], dy = q[1] - p[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len;                       // inward (left) normal of a CCW edge
    return { p: [p[0] + nx * d, p[1] + ny * d] as V2, d: [dx, dy] as V2 };
  });
  return points.map((_, i) => {
    const l0 = lines[(i - 1 + n) % n], l1 = lines[i];
    const det = l0.d[0] * l1.d[1] - l0.d[1] * l1.d[0];
    if (Math.abs(det) < 1e-9) return l1.p;
    const t = ((l1.p[0] - l0.p[0]) * l1.d[1] - (l1.p[1] - l0.p[1]) * l1.d[0]) / det;
    return [l0.p[0] + l0.d[0] * t, l0.p[1] + l0.d[1] * t] as V2;
  });
}

function geometryFrom(pos: number[], uvScale = 1): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();                                   // non-indexed → flat facets
  const uv: number[] = [];
  for (let i = 0; i < pos.length; i += 3) uv.push(pos[i] * uvScale, pos[i + 1] * uvScale);
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  return g;
}

/** A convex CCW polygon raised as a chamfered plateau facing +z: outline at z = 0, top (inset by `chamfer`) at z = `depth`. */
export function plateau(points: V2[], depth: number, chamfer: number): THREE.BufferGeometry {
  const top = insetConvex(points, chamfer);
  const pos: number[] = [];
  const tri = (p: V3, q: V3, r: V3) => pos.push(...p, ...q, ...r);
  for (let i = 1; i < top.length - 1; i++) tri([...top[0], depth], [...top[i], depth], [...top[i + 1], depth]);
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const a: V3 = [...points[i], 0], b: V3 = [...points[j], 0], c: V3 = [...top[j], depth], d: V3 = [...top[i], depth];
    tri(a, b, c); tri(a, c, d);
  }
  return geometryFrom(pos);
}

/**
 * A faceted relief facing +z: vertices [x, y, height], an outline (CCW indices) walled down to z = 0, and top
 * triangles. Used for the ETH crystal.
 */
export function relief(verts: V3[], outline: number[], tops: [number, number, number][]): THREE.BufferGeometry {
  const pos: number[] = [];
  const up = new THREE.Vector3(0, 0, 1);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const facing = (p: V3, q: V3, r: V3, want: THREE.Vector3) => {
    a.fromArray(p); b.fromArray(q); c.fromArray(r);
    const n = b.clone().sub(a).cross(c.clone().sub(a));
    if (n.dot(want) >= 0) pos.push(...p, ...q, ...r); else pos.push(...p, ...r, ...q);
  };
  for (const [i, j, k] of tops) facing(verts[i], verts[j], verts[k], up);
  for (let e = 0; e < outline.length; e++) {
    const p = verts[outline[e]], q = verts[outline[(e + 1) % outline.length]];
    const out = new THREE.Vector3(q[1] - p[1], -(q[0] - p[0]), 0);   // right of a CCW edge = outward
    const p0: V3 = [p[0], p[1], 0], q0: V3 = [q[0], q[1], 0];
    facing(p0, q0, q, out);
    facing(p0, q, p, out);
  }
  return geometryFrom(pos);
}

// ------------------------------------------------------------------------------------------------ logos as geometry

/**
 * The Solana mark as three chamfered bars facing +z, centred, `width` across (height = width × 88/101): top and
 * bottom bars lean right, the middle one left — the mark's own proportions (viewBox 101 × 88, bars 21.8 tall).
 */
export function solMark(width: number, depth: number, chamfer: number): THREE.BufferGeometry {
  return once(`g:solmark:${width}:${depth}:${chamfer}`, () => {
    const s = width / 101;
    const P = (x: number, y: number): V2 => [(x - 50.5) * s, (y - 44) * s];
    const bars: V2[][] = [
      [P(0, 66.2), P(81, 66.2), P(101, 88), P(20, 88)],        // top: bottom edge 0–81, top edge 20–101
      [P(20, 33.1), P(101, 33.1), P(81, 54.9), P(0, 54.9)],    // middle: leans the other way
      [P(0, 0), P(81, 0), P(101, 21.8), P(20, 21.8)]           // bottom
    ];
    const parts = bars.map((bar) => plateau(bar, depth, chamfer));
    return mergeGeometries(parts, false) ?? parts[0];
  });
}

/**
 * The ETH diamond as a faceted crystal facing +z, centred, `height` tall (the logo's 256 × 417 artwork): an upper
 * kite with a raised centre ridge and the lower chevron folded along its spine.
 */
export function ethMark(height: number, depth: number): THREE.BufferGeometry {
  return once(`g:ethmark:${height}:${depth}`, () => {
    const s = height / 417;
    const wall = 0.32, ridge = 0.62;
    const V = (x: number, y: number, h: number): V3 => [(x - 128) * s, (y - 208.5) * s, h * depth];
    const kite = relief(
      [V(128, 417, ridge), V(0, 204.7, wall), V(128, 129, ridge), V(256, 204.7, wall), V(128, 262.8, 1)],
      [0, 1, 2, 3],
      [[0, 1, 4], [1, 2, 4], [2, 3, 4], [3, 0, 4]]
    );
    const chevron = relief(
      [V(0, 180.4, wall), V(128, 0, 0.62), V(256, 180.4, wall), V(128, 104.8, 0.86)],
      [0, 1, 2, 3],
      [[0, 1, 3], [1, 2, 3]]
    );
    return mergeGeometries([kite, chevron], false) ?? kite;
  });
}

// ------------------------------------------------------------------------------------------------ helpers for builders

/** A helix spring along +y from y = 0 to `height` (a tube). */
export function spring(radius: number, wire: number, turns: number, height: number, perTurn = 14, radial = 6): THREE.BufferGeometry {
  return once(`g:spring:${radius}:${wire}:${turns}:${height}:${perTurn}:${radial}`, () => {
    class Helix extends THREE.Curve<THREE.Vector3> {
      constructor() { super(); }
      getPoint(t: number, target = new THREE.Vector3()): THREE.Vector3 {
        const a = t * turns * Math.PI * 2;
        return target.set(Math.cos(a) * radius, t * height, Math.sin(a) * radius);
      }
    }
    return new THREE.TubeGeometry(new Helix(), Math.max(8, Math.round(turns * perTurn)), wire, radial, false);
  });
}

/** A side profile in (z, y) extruded across x, centred on x, edges rounded by a bevel. Corners listed in `round` are curved. */
export function profileSlab(points: V2[], width: number, bevel: number, opts: { round?: number[]; curveSegments?: number; bevelSegments?: number } = {}): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const round = new Set(opts.round ?? []);
  const n = points.length;
  const mid = (a: V2, b: V2): V2 => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  // start at the midpoint of the last edge so every corner can be rounded
  const start = mid(points[n - 1], points[0]);
  shape.moveTo(start[0], start[1]);
  for (let i = 0; i < n; i++) {
    const p = points[i], next = points[(i + 1) % n];
    const m = mid(p, next);
    if (round.has(i)) shape.quadraticCurveTo(p[0], p[1], m[0], m[1]);
    else { shape.lineTo(p[0], p[1]); shape.lineTo(m[0], m[1]); }
  }
  const depth = Math.max(0.005, width - bevel * 2);
  // bevelOffset = −bevelSize keeps the outline exact (the bevel rounds inwards instead of growing the shape)
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel * 0.9, bevelOffset: -bevel * 0.9, bevelSegments: opts.bevelSegments ?? 2, curveSegments: opts.curveSegments ?? 6 });
  g.translate(0, 0, -depth / 2);
  g.rotateY(-Math.PI / 2);                                    // shape x → world z, extrusion → world x
  return g;
}

/** A flat shape (in x/y) extruded along +z with a bevel (outline kept exact); returned centred on z. */
export function slab(shape: THREE.Shape, thickness: number, bevel: number, curveSegments = 8, bevelSegments = 2): THREE.BufferGeometry {
  const depth = Math.max(0.002, thickness - bevel * 2);
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel, bevelOffset: -bevel, bevelSegments, curveSegments });
  g.translate(0, 0, -depth / 2);
  return g;
}

/** A rounded rectangle path centred on the origin. */
export function roundRect(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  const rr = Math.min(r, w / 2, h / 2);
  s.moveTo(x + rr, y);
  s.lineTo(x + w - rr, y);
  s.quadraticCurveTo(x + w, y, x + w, y + rr);
  s.lineTo(x + w, y + h - rr);
  s.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  s.lineTo(x + rr, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - rr);
  s.lineTo(x, y + rr);
  s.quadraticCurveTo(x, y, x + rr, y);
  return s;
}

/** A plane (front = +z) whose UVs repeat `u` × `v` times (tiling normal maps in metres). */
export function tiledPlane(w: number, h: number, u: number, v: number): THREE.BufferGeometry {
  return once(`g:tiled:${w}:${h}:${u}:${v}`, () => {
    const g = new THREE.PlaneGeometry(w, h);
    const uv = g.getAttribute("uv") as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * u, uv.getY(i) * v);
    return g;
  });
}

/** Draw calls an object costs (visible meshes and sprites). */
export function drawCalls(object: THREE.Object3D): number {
  let n = 0;
  object.traverseVisible((o) => { if ((o as THREE.Mesh).isMesh || (o as THREE.Sprite).isSprite) n++; });
  return n;
}

/** Triangles an object draws in the colour pass (all meshes, within each geometry's draw range). */
export function triangles(object: THREE.Object3D): number {
  let n = 0;
  object.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const g = mesh.geometry;
    const all = g.index ? g.index.count : g.getAttribute("position").count;
    n += Math.min(all, Math.max(0, all - g.drawRange.start), g.drawRange.count) / 3;     // the colour pass's range (a shadow carrier's proxy is not drawn here)
  });
  return Math.round(n);
}
